<template>
    <button type="button" :class="[themeClasses[theme], sizeClass, iconOnlyClass]">
        <slot></slot>
    </button>
</template>

<script lang="ts">
export type ButtonSize = "sm" | "compact" | "md";
export type ButtonTheme = "primary" | "secondary" | "tertiary" | "neutral" | "cream" | "danger" | "startChat";

interface BaseButtonProps extends /* @vue-ignore */ ButtonHTMLAttributes {
    theme: ButtonTheme;
    size?: ButtonSize;
    /** Square button holding a single icon. Pass an `aria-label`, since the icon is the only content. */
    iconOnly?: boolean;
}
</script>

<script setup lang="ts">
import { invariant } from "~vue/utils";
import type { ButtonHTMLAttributes } from "vue";
import { computed, useAttrs } from "vue";

const { theme, size = "md", iconOnly = false } = defineProps<BaseButtonProps>();
const attrs = useAttrs();

invariant(
    !iconOnly || !!attrs["aria-label"] || !!attrs["aria-labelledby"] || !!attrs.title,
    "BaseButton: icon-only buttons need an aria-label (or aria-labelledby / title) so screen readers can name them.",
);

const themeClasses: Record<ButtonTheme, string> = {
    neutral: "base-btn-neutral",
    cream: "base-btn-cream",
    // `startChat` isn't exactly a theme, but this is temp until we have a design system for button types
    startChat: "btn-start-chat",
    primary: "base-btn-primary",
    secondary: "base-btn-secondary",
    tertiary: "base-btn-tertiary",
    danger: "base-btn-danger",
};

const sizeClass = computed(() => {
    // Icon-only buttons size themselves from their own padding tokens, not the text-button ones.
    if (iconOnly) {
        return "";
    }
    switch (size) {
        case "sm":
            return "base-btn-sm";
        default:
            return "";
    }
});

const iconOnlyClass = computed(() => {
    if (!iconOnly) {
        return "";
    }
    return size === "sm" ? "base-btn-icon-only base-btn-icon-only-sm" : "base-btn-icon-only";
});
</script>
